// Subscriber operational model: service (WHMCS) and network (ONT, CPE) kept
// apart, then compared. Pure; checked by subscribers.check.ts.

import { cpeState, lastOutage, ontLive } from "./ontstate";
import { classifyRx, type SignalBands } from "./signal";
import type { Subscriber, SubscriberONT } from "./types";

// ---------- service ----------

export type Svc = "active" | "suspended" | "terminated" | "cancelled" | "other";
export const svcOf = (status: string): Svc => {
  const s = status.toLowerCase();
  return s === "active" || s === "suspended" || s === "terminated" || s === "cancelled" ? s : "other";
};
/** Services the NOC is expected to keep running. */
export const isOperational = (s: Subscriber) => {
  const v = svcOf(s.status);
  return v === "active" || v === "suspended";
};

/** "New AthaHomeNet 2 - 35 Mbps Broadband" -> { name: "New AthaHomeNet 2", speed: "35 Mbps" }. */
export function planOf(plan: string | null): { name: string; speed: string | null } {
  const raw = (plan ?? "").trim();
  if (!raw) return { name: "", speed: null };
  const m = raw.match(/(\d+(?:[.,]\d+)?)\s*([KMG])bps/i);
  if (!m) return { name: raw, speed: null };
  const speed = `${m[1]} ${m[2].toUpperCase()}bps`;
  const before = raw.slice(0, m.index).replace(/[\s\-–:|]+$/, "").trim();
  const after = raw.slice(m.index! + m[0].length).replace(/^[\s\-–:|]+/, "").replace(/\(.*?\)/g, "").trim();
  return { name: before || after || raw, speed };
}

// ---------- network ----------

/**
 * Where the service is on the network now, worst first. ONT states come from
 * the OLT; cpe_* from GenieACS informs only, when no ONT is linked -- a CPE
 * that stops informing is "silent", which is evidence, not proof, of an outage.
 */
export type Net = "los" | "power" | "offline" | "critical" | "weak" | "stale" | "cpe_silent" | "unknown" | "online" | "cpe_online" | "unlinked";
export const NET_RANK: Record<Net, number> = {
  los: 0, power: 0, offline: 0, critical: 1, weak: 2, stale: 3, cpe_silent: 4, unknown: 5, online: 6, cpe_online: 7, unlinked: 8,
};
export const isDown = (n: Net) => n === "los" || n === "power" || n === "offline";
export const isUp = (n: Net) => n === "online" || n === "weak" || n === "critical" || n === "cpe_online";

export type Ctx = { pollS: number; now: number; bands: SignalBands };
export type OntNet = { ont: SubscriberONT; net: Net; downMs: number | null };

export function ontNet(o: SubscriberONT, c: Ctx): OntNet {
  const live = ontLive(o, c.pollS, c.now);
  if (live === "offline" || live === "missing") {
    const out = lastOutage(o, c.now);
    const net: Net = out?.kind === "los" ? "los" : out?.kind === "dyingGasp" ? "power" : "offline";
    return { ont: o, net, downMs: out?.ongoing ? out.durationMs : null };
  }
  if (live === "stale") return { ont: o, net: "stale", downMs: null };
  if (live === "unknown") return { ont: o, net: "unknown", downMs: null };
  const lvl = classifyRx(o.rx_power_dbm, c.bands);
  return { ont: o, net: lvl === "crit" ? "critical" : lvl === "warn" ? "weak" : "online", downMs: null };
}

export type NetSummary = {
  net: Net;
  source: "ont" | "cpe" | "none";
  /** The ONT the summary speaks for (the worst one). */
  primary: OntNet | null;
  onts: OntNet[];
  up: number;
  down: number;
  /** How long it has been down, or the CPE silent. */
  forMs: number | null;
  rx: number | null;
};

export function netOf(s: Subscriber, c: Ctx): NetSummary {
  if (s.onts.length) {
    const onts = s.onts.map((o) => ontNet(o, c)).sort((a, b) => NET_RANK[a.net] - NET_RANK[b.net]);
    const p = onts[0];
    return {
      net: p.net, source: "ont", primary: p, onts,
      up: onts.filter((x) => isUp(x.net)).length, down: onts.filter((x) => isDown(x.net)).length,
      forMs: p.downMs, rx: isUp(p.net) ? p.ont.rx_power_dbm : null,
    };
  }
  const base = { primary: null, onts: [], up: 0, down: 0, rx: null };
  if (!s.cpe) return { ...base, net: "unlinked", source: "none", forMs: null };
  const st = cpeState(s.cpe, true, c.now);
  const since = s.cpe.last_inform_at ? c.now - Date.parse(s.cpe.last_inform_at) : null;
  return { ...base, net: st === "online" ? "cpe_online" : "cpe_silent", source: "cpe", forMs: st === "online" ? null : since };
}

// ---------- service vs network ----------

/** A disagreement between billing and the network that someone should look at. */
export type Attention = "activeDown" | "activeSignal" | "activeSilent" | "suspendedOnline" | "endedLinked";
export const ATTENTION_RANK: Record<Attention, number> = { activeDown: 0, activeSignal: 1, activeSilent: 2, suspendedOnline: 3, endedLinked: 4 };

/**
 * Active + unlinked is deliberately not here: it is a data gap (hundreds of
 * rows until linking catches up), reachable through the Unlinked filter.
 */
export function attentionOf(s: Subscriber, n: NetSummary): Attention | null {
  const svc = svcOf(s.status);
  if (svc === "active") {
    if (isDown(n.net)) return "activeDown";
    if (n.net === "critical" || n.net === "weak") return "activeSignal";
    if (n.net === "cpe_silent") return "activeSilent";
    return null;
  }
  if (svc === "suspended") return isUp(n.net) ? "suspendedOnline" : null;
  if ((svc === "terminated" || svc === "cancelled") && (s.onts.length > 0 || n.net === "cpe_online")) return "endedLinked";
  return null;
}

// ---------- view: filters, search, sort ----------

export type Quick = "attention" | "active" | "suspended" | "unlinked" | "offline" | "weak" | "all";
export type Scope = "operational" | "all";
export type SortKey = "name" | "id" | "service" | "status" | "network" | "signal" | "down";
export type View = {
  scope: Scope; quick: Quick; q: string; sort: SortKey; dir: "asc" | "desc";
  svc: Svc[]; net: Net[]; olt: string; pon: string; plan: string; odp: string; link: "" | "linked" | "unlinked";
  page: number; size: number;
};

export const DEFAULT_VIEW: View = {
  scope: "operational", quick: "all", q: "", sort: "network", dir: "asc", svc: [], net: [], olt: "", pon: "", plan: "", odp: "", link: "", page: 0, size: 50,
};

const list = <T extends string>(v: string | null, ok: readonly T[]): T[] => (v ? v.split(",").filter((x): x is T => ok.includes(x as T)) : []);
const SVCS = ["active", "suspended", "terminated", "cancelled", "other"] as const;
const NETS = Object.keys(NET_RANK) as Net[];
const QUICKS = ["attention", "active", "suspended", "unlinked", "offline", "weak", "all"] as const;
const SORTS = ["name", "id", "service", "status", "network", "signal", "down"] as const;

/** View <-> URL, only non-defaults written, so a shared link stays short. */
export function viewFromParams(p: URLSearchParams): View {
  const pick = <T extends string>(k: string, ok: readonly T[], d: T): T => (ok.includes(p.get(k) as T) ? (p.get(k) as T) : d);
  return {
    scope: pick("scope", ["operational", "all"] as const, DEFAULT_VIEW.scope),
    quick: pick("f", QUICKS, DEFAULT_VIEW.quick),
    q: p.get("q") ?? "",
    sort: pick("sort", SORTS, DEFAULT_VIEW.sort),
    dir: p.get("dir") === "desc" ? "desc" : "asc",
    svc: list(p.get("status"), SVCS), net: list(p.get("network"), NETS),
    olt: p.get("olt") ?? "", pon: p.get("pon") ?? "", plan: p.get("plan") ?? "", odp: p.get("odp") ?? "",
    link: pick("link", ["", "linked", "unlinked"] as const, ""),
    page: Math.max(0, Number(p.get("page") ?? 1) - 1 || 0),
    size: [25, 50, 100].includes(Number(p.get("size"))) ? Number(p.get("size")) : DEFAULT_VIEW.size,
  };
}

export function viewToParams(v: View): URLSearchParams {
  const p = new URLSearchParams();
  const set = (k: string, val: string, d: string) => { if (val !== d) p.set(k, val); };
  set("scope", v.scope, DEFAULT_VIEW.scope);
  set("f", v.quick, DEFAULT_VIEW.quick);
  set("q", v.q, "");
  set("sort", v.sort, DEFAULT_VIEW.sort);
  set("dir", v.dir, "asc");
  set("status", v.svc.join(","), "");
  set("network", v.net.join(","), "");
  set("olt", v.olt, "");
  set("pon", v.pon, "");
  set("plan", v.plan, "");
  set("odp", v.odp, "");
  set("link", v.link, "");
  set("page", String(v.page + 1), "1");
  set("size", String(v.size), String(DEFAULT_VIEW.size));
  return p;
}

export type Row = { s: Subscriber; n: NetSummary; att: Attention | null; plan: ReturnType<typeof planOf>; svc: Svc };

export const rowOf = (s: Subscriber, c: Ctx): Row => {
  const n = netOf(s, c);
  return { s, n, att: attentionOf(s, n), plan: planOf(s.plan_code), svc: svcOf(s.status) };
};

/** Everything an operator might paste: name, codes, logins, serials, IPs, MACs, OLT/PON. */
export function haystack(s: Subscriber): string {
  const parts: (string | number | null | undefined)[] = [
    s.full_name, s.customer_code, s.external_id, s.whmcs_client_id, s.pppoe_username, s.plan_code,
    s.cpe?.serial, s.cpe?.wan_ip, s.cpe?.mac, s.cpe?.tag_login, s.cpe?.tag_name, s.cpe?.odp,
  ];
  for (const o of s.onts) parts.push(o.serial, o.description, o.olt_name, o.pon_path, `${o.pon_path}:${o.ont_id}`);
  return parts.filter((x) => x !== null && x !== undefined && x !== "").join(" ").toLowerCase();
}

export const quickTest: Record<Quick, (r: Row) => boolean> = {
  attention: (r) => r.att !== null,
  active: (r) => r.svc === "active",
  suspended: (r) => r.svc === "suspended",
  unlinked: (r) => r.n.net === "unlinked",
  offline: (r) => isDown(r.n.net) || r.n.net === "cpe_silent",
  weak: (r) => r.n.net === "weak" || r.n.net === "critical",
  all: () => true,
};

/** Rows in scope and matching the advanced filters and search, before the quick filter. */
export function baseFilter(rows: Row[], v: View): Row[] {
  const words = v.q.toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((r) => {
    if (v.scope === "operational" && !isOperational(r.s)) return false;
    if (v.svc.length && !v.svc.includes(r.svc)) return false;
    if (v.net.length && !v.net.includes(r.n.net)) return false;
    if (v.olt && !r.s.onts.some((o) => String(o.olt_id) === v.olt)) return false;
    if (v.pon && !r.s.onts.some((o) => `${o.olt_id}:${o.pon_path}` === v.pon)) return false;
    if (v.plan && r.plan.name !== v.plan) return false;
    if (v.odp && !odpsOf(r.s).includes(v.odp)) return false;
    if (v.link === "linked" && !r.s.onts.length) return false;
    if (v.link === "unlinked" && r.s.onts.length) return false;
    if (words.length) {
      const h = haystack(r.s);
      if (!words.every((w) => h.includes(w))) return false;
    }
    return true;
  });
}

const SVC_RANK: Record<Svc, number> = { active: 0, suspended: 1, other: 2, terminated: 3, cancelled: 4 };
const speedBps = (p: { speed: string | null }) => {
  const m = p.speed?.match(/([\d.,]+)\s*([KMG])/);
  return m ? Number(m[1].replace(",", ".")) * ({ K: 1e3, M: 1e6, G: 1e9 } as Record<string, number>)[m[2]] : -1;
};
const codeNum = (s: Subscriber) => Number(s.external_id ?? s.customer_code?.replace(/\D/g, "") ?? 0) || 0;

/** Sort; nulls (no reading, not down) always last whatever the direction. */
export function sortRows(rows: Row[], key: SortKey, dir: "asc" | "desc"): Row[] {
  const k = dir === "asc" ? 1 : -1;
  const name = (a: Row, b: Row) => a.s.full_name.localeCompare(b.s.full_name);
  const nullsLast = (x: number | null, y: number | null) => (x === null ? (y === null ? 0 : 1) : y === null ? -1 : k * (x - y));
  const cmp: Record<SortKey, (a: Row, b: Row) => number> = {
    name: (a, b) => k * name(a, b),
    id: (a, b) => k * (codeNum(a.s) - codeNum(b.s)),
    service: (a, b) => k * (a.plan.name.localeCompare(b.plan.name) || speedBps(a.plan) - speedBps(b.plan)),
    status: (a, b) => k * (SVC_RANK[a.svc] - SVC_RANK[b.svc]),
    // Worst first; ties by attention, then longest down.
    network: (a, b) => k * (NET_RANK[a.n.net] - NET_RANK[b.n.net]) || (a.att === null ? 1 : 0) - (b.att === null ? 1 : 0) || (b.n.forMs ?? 0) - (a.n.forMs ?? 0),
    // "asc" = weakest first.
    signal: (a, b) => nullsLast(a.n.rx, b.n.rx),
    // "desc" = down longest first; asc puts the most recent drops first.
    down: (a, b) => nullsLast(a.n.forMs, b.n.forMs),
  };
  return [...rows].sort((a, b) => cmp[key](a, b) || name(a, b));
}

// ---------- ODP correlation ----------

/** The ODPs a subscriber's drop hangs off, from its CPE's odp-* tags. */
export const odpsOf = (s: Subscriber): string[] => (s.cpe?.odp ? s.cpe.odp.split(",").filter(Boolean) : []);

export type OdpIssue = { odp: string; down: number; total: number };

/**
 * ODPs where several operational services are down together: one splitter or
 * feeder fault looks like a string of unrelated customer outages. At least two
 * down, and at least half of the ODP's known services. A hint, not a diagnosis.
 */
export function odpIssues(rows: Row[]): OdpIssue[] {
  const by = new Map<string, { down: number; total: number }>();
  for (const r of rows) {
    if (!isOperational(r.s) || r.n.source === "none") continue;
    const down = isDown(r.n.net) || r.n.net === "cpe_silent";
    for (const odp of odpsOf(r.s)) {
      const x = by.get(odp) ?? { down: 0, total: 0 };
      x.total++;
      if (down) x.down++;
      by.set(odp, x);
    }
  }
  return [...by.entries()].filter(([, x]) => x.down >= 2 && x.down * 2 >= x.total)
    .map(([odp, x]) => ({ odp, ...x })).sort((a, b) => b.down - a.down || a.odp.localeCompare(b.odp));
}
